import {
  autoUpdate,
  ClientRectObject,
  ReferenceType,
  UseFloatingReturn,
} from '@floating-ui/react';
import { toDOMRange } from 'lib-knowledge-document-structure/plate/common';
import { MyEditor } from 'lib-knowledge-document-structure/plateTypes';
import { BasePoint } from 'lib-knowledge-document-structure/slate/package';
import { useEffect } from 'react';

export const editorHeadersHeight = 64 + 48; // Size of the two heading elements

/**
 * Default value to use for VirtualElement.
 *
 * @returns
 */
export const getDefaultBoundingClientRect = (): ClientRectObject => ({
  width: 0,
  height: 0,
  x: 0,
  y: 0,
  top: -9999,
  left: -9999,
  right: 9999,
  bottom: 9999,
});

/**
 * Get bounding client rect of the window selection. If no selection then return
 * a default rect that is offscreen.
 */
export const getSelectionBoundingClientRect = (): ClientRectObject => {
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount < 1) {
    return getDefaultBoundingClientRect();
  }
  const domRange = domSelection.getRangeAt(0);
  return domRange.getBoundingClientRect();
};

/**
 * Get bounding client rect of the point passed in. If not point is passed then
 * return a default rect that is offscreen.
 */
export const getPointBoundingClientRect = (
  editor: MyEditor,
  point: BasePoint | null,
) => {
  return (): ClientRectObject => {
    if (!point) {
      return getDefaultBoundingClientRect();
    }
    const range =
      toDOMRange(editor, {
        focus: point,
        anchor: {
          path: point.path,
          offset: point.offset - 1,
        },
      }) ?? document.getSelection()?.getRangeAt(0); // Try to fallback to the current selection range.
    if (!range) {
      return getDefaultBoundingClientRect();
    }

    return range.getBoundingClientRect();
  };
};

/**
 * Only run autoUpdate when isOpen. This allows us to keep the FloatingDiv on
 * the screen and animate the contents.
 */
export function useToggleAutoAttach({
  isOpen,
  update,
  floatingDivId,
  virtualElementRef,
}: {
  isOpen: boolean;
  update: UseFloatingReturn['update'];
  floatingDivId: string;
  virtualElementRef: ReferenceType;
}): void {
  useEffect(
    function attachAutoUpdate() {
      const floatingDiv = document.getElementById(floatingDivId);
      if (!isOpen || !floatingDiv) return;
      const cleanup = autoUpdate(virtualElementRef, floatingDiv, update);
      return cleanup;
    },
    [isOpen, update, floatingDivId, virtualElementRef],
  );
}

/**
 * This is the common return type for the hover hooks. This can be directly
 * passed to FloatingDiv.
 */
export type FloatingProps = UseFloatingReturn & {
  hide?: boolean;
  isOpen?: boolean;
};
